import React, { useEffect, useMemo } from "react";

import { Equipment } from "domain/entities/equipment";
import { useEquipmentAttributeDefinitions } from "presentation/hooks/use-query-equipments";
import { useUserStore } from "presentation/stores/use-user-store";
import { parseEquipmentAttributes } from "./equipment-utils";
import EquipmentAttributesTab from "./EquipmentAttributesTab";

interface EquipmentAttributesFormProps {
  selectedEquipment: Equipment;
  attributesObject: Record<string, any>;
  setAttributesObject: (attrs: Record<string, any>) => void;
  isAdmin?: boolean;
  tabContentStyle?: React.CSSProperties;
  dropdownLabelStyle?: React.CSSProperties;
}

const defaultTabContentStyle: React.CSSProperties = {
  minHeight: "60vh",
};

const defaultDropdownLabelStyle: React.CSSProperties = {
  marginTop: "-1rem",
  fontSize: "smaller",
};

const EquipmentAttributesForm: React.FC<EquipmentAttributesFormProps> = ({
  selectedEquipment,
  attributesObject,
  setAttributesObject,
  isAdmin = false,
  tabContentStyle = defaultTabContentStyle,
  dropdownLabelStyle = defaultDropdownLabelStyle,
}) => {
  const { user } = useUserStore();
  const isAdminOrPatrimonio =
    isAdmin ||
    user?.roles?.some((role) =>
      ["admin", "patrimonio"].includes(role.name.toLowerCase()),
    ) ||
    user?.profileType?.toLowerCase() === "admin";

  const { data: attrDefsResponse, isLoading: loadingAttrDefs } =
    useEquipmentAttributeDefinitions(
      { equipmentTypeId: selectedEquipment?.equipmentTypeId },
      !!selectedEquipment?.equipmentTypeId,
    );

  const visibleAttrDefs = useMemo(() => {
    return (attrDefsResponse?.data?.data || []).filter(
      (definition) => isAdminOrPatrimonio || definition.isUserEditable,
    );
  }, [attrDefsResponse?.data?.data, isAdminOrPatrimonio]);

  useEffect(() => {
    const storedAttributes = parseEquipmentAttributes(
      selectedEquipment?.attributes,
    );
    const nextAttributes = visibleAttrDefs.reduce(
      (accumulator, definition) => {
        const currentValue = attributesObject?.[definition.name];
        const storedValue = storedAttributes?.[definition.name];

        accumulator[definition.name] =
          currentValue !== undefined ? currentValue : (storedValue ?? "");

        return accumulator;
      },
      {} as Record<string, any>,
    );

    if (JSON.stringify(nextAttributes) !== JSON.stringify(attributesObject)) {
      setAttributesObject(nextAttributes);
    }
  }, [
    visibleAttrDefs,
    selectedEquipment?.attributes,
    attributesObject,
    setAttributesObject,
  ]);

  return (
    <EquipmentAttributesTab
      loading={loadingAttrDefs}
      visibleAttrDefs={visibleAttrDefs}
      attributesObject={attributesObject}
      setAttributesObject={setAttributesObject}
      isAdminOrPatrimonio={isAdminOrPatrimonio}
      tabContentStyle={tabContentStyle}
      dropdownLabelStyle={dropdownLabelStyle}
    />
  );
};

export default EquipmentAttributesForm;
